import React, { useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import tenantService from '../../services/tenantService';
import { useToast } from '../../contexts/ToastContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const TenantDashboard = () => {
  const [tenantInfo, setTenantInfo] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [dashboardData, setDashboardData] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showError } = useToast();

  const fetchTenantDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [info, stats, dashboard, notifs] = await Promise.allSettled([
        tenantService.getTenantInfo(),
        tenantService.getTenantStatistics(),
        tenantService.getTenantDashboardData(),
        tenantService.getTenantNotifications()
      ]);

      setTenantInfo(info.status === 'fulfilled' ? info.value : {});
      setStatistics(stats.status === 'fulfilled' ? stats.value : {});
      setDashboardData(dashboard.status === 'fulfilled' ? dashboard.value : {});
      setNotifications(notifs.status === 'fulfilled' ? notifs.value : []);
      
    } catch (error) {
      showError('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchTenantDashboardData();
  }, [fetchTenantDashboardData]);

  const currency = (
    tenantInfo?.settings?.general?.currency ||
    tenantInfo?.tenant?.currency ||
    statistics.currency ||
    dashboardData.currency ||
    'HTG'
  );

  const formatMoney = (value) => `${(value || 0).toLocaleString('fr-FR')} ${currency}`;

  const performanceData = dashboardData.monthly_performance || [];

  const StatCard = ({ title, value, icon: Icon, trend, subtitle, color = 'primary' }) => {
    const colorClasses = {
      primary: 'bg-primary-50 text-primary-600',
      success: 'bg-green-50 text-green-600',
      warning: 'bg-yellow-50 text-yellow-600',
      danger: 'bg-red-50 text-red-600',
    };

    return (
      <div className="card hover:shadow-lg transition-shadow duration-300">
        <div className="p-6">
          <div className="flex items-center">
            <div className={`flex-shrink-0 rounded-md p-3 ${colorClasses[color]}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
              </p>
              {trend !== undefined && (
                <div className="flex items-center mt-1">
                  {trend > 0 ? (
                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
                  )}
                  <span className={`text-sm ml-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {trend > 0 ? '+' : ''}{trend}%
                  </span>
                  {subtitle && (
                    <span className="text-sm text-gray-500 ml-2">{subtitle}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Tableau de bord - {tenantInfo?.tenant?.name || 'Organisation'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Vue d'ensemble de votre organisation
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Clients"
          value={statistics.total_clients || 0}
          icon={UsersIcon}
          trend={statistics.monthly_growth}
          subtitle="ce mois"
        />
        <StatCard
          title="Épargne Totale"
          value={formatMoney(statistics.total_savings)}
          icon={CreditCardIcon}
          subtitle="Solde réel des comptes"
          color="success"
        />
        <StatCard
          title="Portefeuille Prêts"
          value={formatMoney(statistics.total_loans)}
          icon={CurrencyDollarIcon}
          subtitle={`${statistics.active_loans || 0} prêts actifs`}
          color="warning"
        />
        <StatCard
          title="Taux de Recouvrement"
          value={`${statistics.collection_rate || 0}%`}
          icon={ChartBarIcon}
          subtitle={`${statistics.default_rate || 0}% défaillance`}
          color={statistics.collection_rate > 90 ? 'success' : 'danger'}
        />
      </div>

      {/* Charts and Notifications */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Performance Chart */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Décaissements et recouvrements réels
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => [formatMoney(value), 'Montant']}
                      labelFormatter={(label) => `Mois: ${label}`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="disbursed"
                      name="Décaissé"
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="collected"
                      name="Recouvré"
                      stroke="#ef4444" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
                <BellIcon className="h-5 w-5 text-gray-400" />
              </div>
              
              <div className="space-y-4">
                {notifications.length > 0 ? (
                  notifications.slice(0, 5).map((notification, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded-lg ${!notification.read ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}
                    >
                      <div className="flex justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {notification.title}
                        </p>
                        <span className="text-xs text-gray-500">
                          {format(new Date(notification.created_at), 'HH:mm')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {notification.message}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">Aucune notification.</p>
                )}
                
                {notifications.length > 5 && (
                  <button className="w-full text-sm text-primary-600 hover:text-primary-900 text-center py-2">
                    Voir toutes les notifications
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Quick Actions */}
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Actions rapides
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <Link to="/clients/new" className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200">
                <UsersIcon className="h-8 w-8 text-primary-600" />
                <span className="mt-2 text-sm font-medium text-gray-900">Nouveau client</span>
              </Link>
              <Link to="/loans/new" className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200">
                <CurrencyDollarIcon className="h-8 w-8 text-primary-600" />
                <span className="mt-2 text-sm font-medium text-gray-900">Nouveau prêt</span>
              </Link>
              <Link to="/agencies/new" className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200">
                <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
                <span className="mt-2 text-sm font-medium text-gray-900">Nouvelle agence</span>
              </Link>
              <Link to="/reports" className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200">
                <ChartBarIcon className="h-8 w-8 text-primary-600" />
                <span className="mt-2 text-sm font-medium text-gray-900">Rapports</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Activité récente
            </h3>
            <div className="space-y-4">
              {dashboardData.recent_activity?.map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center
                      ${activity.type === 'new_client' ? 'bg-green-100' : ''}
                      ${activity.type === 'loan_approved' ? 'bg-blue-100' : ''}
                    `}>
                      {activity.type === 'new_client' && <UsersIcon className="h-4 w-4 text-green-600" />}
                      {activity.type === 'loan_approved' && <CurrencyDollarIcon className="h-4 w-4 text-blue-600" />}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                  {activity.amount && (
                    <span className="text-sm font-medium text-gray-900">
                      {formatMoney(activity.amount)}
                    </span>
                  )}
                </div>
              ))}
              {!dashboardData.recent_activity?.length && <p className="text-sm text-gray-500">Aucune activité récente.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantDashboard;
